import React, { Component } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { loadProductImages } from '../utils/productImages';

import GridImagesComponent from './GridImagesComponent';

// Selector de imágenes de producto: recorte + grilla reordenable.
//
// Existe porque esto vivía sólo dentro de FormBoxGameComponent (juegos de mesa), y los usados
// no tenían forma de cargar imágenes. En vez de copiar el bloque, se extrae acá para que el
// que lo necesite lo monte; FormBoxGameComponent queda como está (funciona y es riesgoso
// tocarlo) y se puede migrar después.
//
// Contrato: el componente maneja el mapa de imágenes y avisa por `onImagesChange`. El que lo
// usa se encarga de SUBIRLAS, porque el momento de la subida depende de la pantalla (en el alta
// hay que esperar a que el backend devuelva el código).
//
// `storageFolder` (opcional): si viene, carga las imágenes que ya están en el bucket. Es para
// la edición — en el alta todavía no hay carpeta porque no hay código.
//
// `onLoadingChange(bool)` (opcional): avisa mientras las imágenes existentes están EN VUELO. El
// que edita tiene que deshabilitar su guardado hasta que sea false, porque el flujo de edición
// borra la carpeta y reescribe con este mapa: guardarlo a medio llenar borra del bucket lo que
// todavía no llegó.
class ProductImagesPickerComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            src: null,   // imagen elegida, en el recortador
            images: {},  // { [posición]: { path, position } }
            loading: props.storageFolder != null && props.storageFolder !== '',
        };
        this.cropper = null;
    }

    componentDidMount() {
        if (this.props.storageFolder) {
            this.loadExistingImages(this.props.storageFolder);
        }
    }

    componentDidUpdate(prevProps) {
        // La carpeta llega tarde en la edición: la página primero resuelve el código.
        if (this.props.storageFolder && prevProps.storageFolder !== this.props.storageFolder) {
            this.loadExistingImages(this.props.storageFolder);
        }
    }

    // Las imágenes del bucket se traen como data URL, igual que las recién recortadas, para que
    // el resto del flujo no tenga que distinguir "ya estaba" de "es nueva": al guardar se sube
    // todo de nuevo (la edición borra la carpeta y reescribe).
    loadExistingImages = (folder) => {
        this.setLoading(true);
        // `setState` funcional porque las imágenes llegan en paralelo: copiar `this.state.images`
        // haría que dos que resuelven juntas lean el mismo state viejo y una pise a la otra.
        loadProductImages(folder, (position, dataUrl) => {
            this.setState((prev) => ({
                images: { ...prev.images, [position]: { path: dataUrl, position } },
            }), this.emit);
        }).catch(() => {
            // Carpeta inexistente (producto sin imágenes todavía): no es un error.
        })
        // Resuelva o falle, ya no queda nada en vuelo: recién acá el mapa refleja el bucket.
        .finally(() => this.setLoading(false));
    }

    setLoading = (loading) => {
        this.setState({ loading }, () => {
            if (typeof this.props.onLoadingChange === 'function') {
                this.props.onLoadingChange(loading);
            }
        });
    }

    // El thumbnail NO se elige a mano: es la imagen de posición más baja. Se deriva acá y no en
    // cada pantalla porque el storefront lee `{carpeta}/{code}/thumbnail` para la tarjeta del
    // catálogo, y si falta se ve el placeholder aunque la galería tenga fotos.
    withThumbnail = (images) => {
        const result = {};
        let thumbPos = Number.MAX_SAFE_INTEGER;
        let thumbPath = '';
        Object.keys(images).forEach((key) => {
            const img = images[key];
            if (!img || !img.path) return;
            result[img.position] = img;
            if (img.position !== 'thumbnail' && Number(img.position) < thumbPos) {
                thumbPos = Number(img.position);
                thumbPath = img.path;
            }
        });
        if (thumbPath !== '') {
            result.thumbnail = { path: thumbPath, position: 'thumbnail' };
        }
        return result;
    }

    emit = () => {
        if (typeof this.props.onImagesChange === 'function') {
            this.props.onImagesChange(this.withThumbnail(this.state.images));
        }
    }

    onCropperInit = (cropper) => { this.cropper = cropper; }

    onPickFile = (e) => {
        e.preventDefault();
        const files = e.dataTransfer ? e.dataTransfer.files : (e.target && e.target.files);
        if (!files || !files[0]) return;
        const reader = new FileReader();
        reader.onload = () => this.setState({ src: reader.result });
        reader.readAsDataURL(files[0]);
        // Sin esto, elegir DOS VECES el mismo archivo no dispara onChange (el input conserva
        // el value) y parece que el botón dejó de andar.
        e.target.value = '';
    }

    openFileDialog = () => {
        const input = document.getElementById(this.inputId());
        if (input) input.click();
    }

    inputId = () => this.props.inputId || 'product-image-file';

    rotate = () => {
        if (!this.cropper || !this.cropper.getCroppedCanvas()) return;
        this.cropper.rotate(90);
    }

    // Confirma el recorte y lo mete en el primer hueco libre (1..6).
    confirmCrop = () => {
        if (!this.cropper || !this.cropper.getCroppedCanvas()) return;
        const url = this.cropper.getCroppedCanvas({
            maxWidth: 2048,
            maxHeight: 2048,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high',
        }).toDataURL('image/jpeg');

        const images = { ...this.state.images };
        const libre = [1, 2, 3, 4, 5, 6].find((pos) => !images[pos] || !images[pos].path);
        if (libre == null) {
            alert('Ya hay 6 imágenes. Borrá alguna para agregar otra.');
            return;
        }
        images[libre] = { path: url, position: libre };
        this.setState({ images, src: null }, this.emit);
    }

    handleUpdatePositions = (images) => this.setState({ images }, this.emit);

    handleImageDeleted = (position) => {
        const images = { ...this.state.images };
        images[position] = '';
        this.setState({ images }, this.emit);
    }

    render() {
        return (
            <div className="card card-small mb-4">
                <div className="card-body p-4">
                    <h6 className="text-uppercase text-muted">Imágenes</h6>
                    <div className="row">
                        <div className="col-md-6">
                            <Cropper
                                style={{ height: 380, width: '100%', background: '#eaeaea' }}
                                aspectRatio={1}
                                guides={true}
                                src={this.state.src}
                                viewMode={1}
                                dragMode="move"
                                rotatable={true}
                                cropBoxMovable={false}
                                onInitialized={this.onCropperInit}
                            />
                            <div className="row mt-3">
                                <div className="col-4">
                                    <input type="file" id={this.inputId()} accept="image/*" style={{ display: 'none' }} onChange={this.onPickFile} />
                                    <button type="button" className="btn btn-outline-primary btn-block" disabled={this.props.disabled} onClick={this.openFileDialog}>Agregar</button>
                                </div>
                                <div className="col-4">
                                    <button type="button" className="btn btn-outline-dark btn-block" disabled={this.props.disabled || !this.state.src} onClick={this.rotate}>Rotar</button>
                                </div>
                                <div className="col-4">
                                    <button type="button" className="btn btn-outline-success btn-block" disabled={this.props.disabled || !this.state.src} onClick={this.confirmCrop}>Confirmar</button>
                                </div>
                            </div>
                            <small className="text-muted d-block mt-2">
                                Elegí una imagen, recortala y confirmá. La primera de la grilla es la que se ve en el catálogo.
                            </small>
                        </div>
                        <div className="col-md-6">
                            <GridImagesComponent
                                images={this.state.images}
                                onUpdatePositions={this.handleUpdatePositions}
                                onImageDeleted={this.handleImageDeleted}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ProductImagesPickerComponent;
